import React, { useState } from 'react';
import { Eye, EyeOff, ArrowRight, AlertCircle, Triangle, User, Lock } from 'lucide-react';
import { signIn } from '../utils/auth';

export { isAuthenticated, clearSession, getSession } from '../utils/auth';

export default function LoginScreen({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [keep,     setKeep]     = useState(true);
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);
  const [shake,    setShake]    = useState(false);

  // The password is checked by the server (Supabase Auth) — nothing about any
  // account's password lives in this code or in the data the browser downloads.
  async function handleSubmit(e) {
    e.preventDefault();
    const u = username.trim(), p = password;
    if (!u || !p) return;
    setLoading(true); setError('');
    try {
      const s = await signIn(u, p, keep);
      onLogin(s.role === 'admin' ? { role: 'admin' } : { role: 'employee', empId: s.empId });
    } catch (err) {
      setError((err && err.message) || 'Incorrect username or password.');
      setShake(true); setTimeout(() => setShake(false), 600);
    }
    setLoading(false);
  }

  const canSubmit = !loading && username.trim() && password;

  const iWrap = { position:'relative', display:'flex', alignItems:'center' };
  const iStyle = err => ({
    width:'100%', padding:'13px 14px 13px 42px', fontSize:14, boxSizing:'border-box',
    background:'#17171A', border: err ? '1px solid rgba(179,106,99,0.6)' : '1px solid rgba(239,235,228,0.14)',
    borderRadius:2, color:'#EFEBE4', outline:'none', fontFamily:'inherit', transition:'border-color .18s',
  });
  const lStyle = { display:'block', fontFamily:"'JetBrains Mono',monospace", fontSize:10, fontWeight:400, color:'rgba(239,235,228,0.5)', textTransform:'uppercase', letterSpacing:'0.16em', marginBottom:9 };

  return (
    <div style={{minHeight:'100vh',background:'#0A0A0C',display:'flex',alignItems:'center',justifyContent:'center',fontFamily:"'Inter Tight',sans-serif",padding:16,boxSizing:'border-box',position:'relative'}}>
      {/* Brass aura */}
      <div style={{position:'fixed',inset:0,pointerEvents:'none',background:'radial-gradient(70% 45% at 50% 0%, rgba(169,137,83,0.06), transparent 62%)'}}/>

      <div style={{position:'relative',zIndex:1,width:'100%',maxWidth:420}}>
        {/* Brand — STR lockup */}
        <div style={{display:'flex',flexDirection:'column',alignItems:'center',marginBottom:34}}>
          <div style={{fontFamily:"'Bodoni Moda','Didot',Georgia,serif",fontSize:40,letterSpacing:'0.34em',textIndent:'0.34em',color:'#EFEBE4',lineHeight:1}}>STR</div>
          <div style={{width:46,height:1,background:'#A98953',opacity:0.55,margin:'15px 0 12px'}}/>
          <div style={{fontFamily:"'JetBrains Mono',monospace",fontSize:9,letterSpacing:'0.34em',textTransform:'uppercase',color:'rgba(239,235,228,0.55)'}}>Strategists</div>
          <p style={{fontFamily:"'JetBrains Mono',monospace",fontSize:9,letterSpacing:'0.18em',textTransform:'uppercase',color:'rgba(239,235,228,0.32)',margin:'18px 0 0'}}>Operations Portal</p>
        </div>

        {/* Card */}
        <div style={{background:'#141417',border:'1px solid rgba(239,235,228,0.12)',borderRadius:3,padding:'2rem',boxShadow:'0 24px 60px rgba(0,0,0,0.5)',animation:shake?'shakeX 0.5s ease':'slideUp 0.3s ease'}}>
          <form onSubmit={handleSubmit} style={{display:'flex',flexDirection:'column',gap:16}}>
            <div>
              <label style={lStyle}>Username</label>
              <div style={iWrap}>
                <User size={16} color="rgba(239,235,228,0.4)" style={{position:'absolute',left:14}}/>
                <input type="text" value={username} onChange={e=>{setUsername(e.target.value);setError('');}} placeholder="Enter your username" autoFocus autoComplete="username" style={iStyle(error)}/>
              </div>
            </div>
            <div>
              <label style={lStyle}>Password</label>
              <div style={iWrap}>
                <Lock size={16} color="rgba(239,235,228,0.4)" style={{position:'absolute',left:14}}/>
                <input type={showPass?'text':'password'} value={password} onChange={e=>{setPassword(e.target.value);setError('');}} placeholder="••••••••" autoComplete="current-password" style={{...iStyle(error),paddingRight:42}}/>
                <button type="button" onClick={()=>setShowPass(v=>!v)} style={{position:'absolute',right:12,background:'none',border:'none',cursor:'pointer',color:'rgba(239,235,228,0.4)',padding:2,display:'flex'}}>
                  {showPass?<EyeOff size={16}/>:<Eye size={16}/>}
                </button>
              </div>
            </div>

            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',fontSize:13}}>
              <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',color:'rgba(239,235,228,0.62)',userSelect:'none'}}>
                <input type="checkbox" checked={keep} onChange={e=>setKeep(e.target.checked)} style={{width:15,height:15,accentColor:'#A98953',cursor:'pointer'}}/>
                Keep me signed in
              </label>
              <span style={{fontFamily:"'JetBrains Mono',monospace",fontSize:11,letterSpacing:'0.04em',color:'#A98953',cursor:'default'}}>Forgot password?</span>
            </div>

            {error && <div className="shake" style={{display:'flex',alignItems:'center',gap:8,background:'rgba(192,128,121,0.08)',border:'1px solid rgba(192,128,121,0.35)',borderRadius:'var(--radius)',padding:'10px 12px'}}><AlertCircle size={15} color="#C08079"/><span style={{fontSize:13,color:'#C08079',fontWeight:400}}>{error}</span></div>}

            <button type="submit" disabled={!canSubmit} style={{width:'100%',padding:14,fontSize:11,fontWeight:400,letterSpacing:'0.16em',textTransform:'uppercase',fontFamily:"'JetBrains Mono',monospace",background:canSubmit?'#A98953':'#1c1c20',color:canSubmit?'#0A0A0C':'rgba(239,235,228,0.35)',border:'none',borderRadius:2,cursor:canSubmit?'pointer':'not-allowed',display:'flex',alignItems:'center',justifyContent:'center',gap:8,marginTop:2,transition:'background .2s'}}>
              {loading?<><span style={{width:14,height:14,border:'2px solid rgba(10,10,12,0.35)',borderTopColor:'#0A0A0C',borderRadius:'50%',animation:'spin 0.7s linear infinite',display:'inline-block'}}/>Signing in…</>:<>Sign In to Dashboard <ArrowRight size={15}/></>}
            </button>
          </form>
        </div>

        <p style={{textAlign:'center',fontSize:12,color:'rgba(239,235,228,0.4)',marginTop:24}}>
          <span style={{cursor:'default'}}>Privacy Policy</span> &nbsp;·&nbsp; <span style={{cursor:'default'}}>Help Center</span> &nbsp;·&nbsp; <span style={{cursor:'default'}}>Terms of Service</span>
        </p>
      </div>

      <style>{`
        @keyframes shakeX{0%,100%{transform:translateX(0)}15%{transform:translateX(-8px)}30%{transform:translateX(8px)}45%{transform:translateX(-6px)}60%{transform:translateX(6px)}75%{transform:translateX(-3px)}90%{transform:translateX(3px)}}
        @keyframes slideUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
        @keyframes spin{to{transform:rotate(360deg)}}
        input::placeholder{color:rgba(239,235,228,0.4)}
        input:focus{border-color:#A98953!important;box-shadow:0 0 0 3px rgba(169,137,83,0.14)}
      `}</style>
    </div>
  );
}
